import {
  useCallback,
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useResizeObserver } from '../../../hooks/useResizeObserver';
import StoreCard, { StoreItemCardRewardType } from '../StoreCard';
import { useAppDispatch, useAppSelector } from '../../../store';
import { ShopItemT } from '../../../types';
import { buyStoreItem } from '../../../store/reducers/store/asyncActions';

type TPurchasableItems = {
  all: ShopItemT[];
  packages: ShopItemT[];
}

function StoreDrawerContent() {
  const dispatch = useAppDispatch();

  const { shopItems } = useAppSelector(({ shop }) => shop);

  const [activeTab, setActiveTab] = useState<'all' | 'packages'>('all');

  const containerRef = useRef<HTMLDivElement>(null);

  const rect = useResizeObserver(containerRef);

  useEffect(() => {
    if (!containerRef.current || !rect) {
      return;
    }
    containerRef.current.style.maxHeight = `${window.innerHeight - 40 - rect.y}px`;
  }, [rect]);

  const purchasableItems = useMemo(() => {
    if (!shopItems?.purchasable) {
      return null;
    }

    return shopItems.purchasable.reduce((dict, item) => {
      const level = item.levels?.[0];

      if ((level?.effects?.length ?? 0) > 1) {
        dict.packages.push(item);
      }

      dict.all.push(item);

      return dict;
    }, { all: [], packages: [] } as TPurchasableItems);
  }, []);

  const storeItemsPower = useMemo(() => {
    const initialState = {
      coins: {
        min: 0,
        max: 0,
      },
      energy: {
        min: 0,
        max: 0,
      },
      packages: {
        min: 0,
        max: 0,
      },
    };

    if (!shopItems?.purchasable) {
      return initialState;
    }

    const dict: {[k: string]: number[]} = {
      coins: [],
      energy: [],
      packages: [],
    };

    shopItems.purchasable.filter((item) => (item.levels?.length ?? 0) > 0).forEach((item) => {
      const level = item.levels?.[0];

      if (!level) {
        return;
      }

      if (level.effects.length > 1) {
        const totalPower = level.effects.reduce((sum, eff) => {
          const val = parseInt(eff.value, 10);

          if (!val) {
            return sum;
          }

          // eslint-disable-next-line no-param-reassign
          sum += val;

          // eslint-disable-next-line consistent-return
          return sum;
        }, 0);

        dict.packages.push(totalPower);
      } else {
        level.effects.forEach((eff) => {
          const val = parseInt(eff.value, 10);

          if (!val) {
            return;
          }

          if (eff.name === 'credit_coins') {
            dict.coins.push(val);
          } else if (eff.name === 'credit_energy') {
            dict.energy.push(val);
          }
        });
      }
    });

    return {
      coins: {
        min: Math.min(...dict.coins),
        max: Math.max(...dict.coins)
        ,
      },
      energy: {
        min: Math.min(...dict.energy),
        max: Math.max(...dict.energy),
      },
      packages: {
        min: Math.min(...dict.packages),
        max: Math.max(...dict.packages),
      },
    };
  }, []);

  const getBoost = ({ energy = 0, coins = 0 }) => {
    let result = 0;

    if (energy && coins) {
      const { min } = storeItemsPower.packages;

      result = ((energy + coins - min) / min) * 100;
    } else if (coins) {
      const { min } = storeItemsPower.coins;

      result = ((coins - min) / min) * 100;
    } else if (energy) {
      const { min } = storeItemsPower.energy;

      result = ((energy - min) / min) * 100;
    }

    return result;
  };

  const onOpenInvoiceLink = (invoiceLink: string) => {
    try {
      window.Telegram.WebApp.openInvoice(invoiceLink, (status: string) => {
        window.console.log(status);
      });
    } catch {
      window.open(invoiceLink, '_blank');
    }
  };

  const onStoreItemPurchase = useCallback(async (storeItem: ShopItemT) => {
    const result = await dispatch(buyStoreItem({ id: storeItem.id, level: 1 }));

    if (result.error) {
      return;
    }

    if (result.payload.type !== 'telegram') {
      return;
    }

    onOpenInvoiceLink(result.payload.data.invoice_link);
  }, []);

  const storeItemRenderer = useCallback((storeItem: ShopItemT, idx: number) => {
    const level = storeItem.levels?.[0];

    if (!level) {
      return null;
    }

    const pkg = {
      id: storeItem.id,
      coins: 0,
      energy: 0,
      boost: 0,
      price: level.price,
      icon: storeItem.icon,
      rewardType: 'common' as StoreItemCardRewardType,
      isPackage: level.effects.length > 1,
      description: storeItem.description,
      name: storeItem.name,
    };

    level.effects.forEach((eff) => {
      if (eff.name === 'credit_coins') {
        pkg.coins += (parseInt(eff.value, 10) || 0);
      } else if (eff.name === 'credit_energy') {
        pkg.energy += (parseInt(eff.value, 10) || 0);
      }
    });

    pkg.boost = getBoost({ coins: pkg.coins, energy: pkg.energy });

    if (storeItem.icon === 'xl-package' || storeItem.icon === 'energy-pack-xl' || storeItem.icon === 'coins-pack-xl') {
      pkg.rewardType = 'gold';
    }

    return <StoreCard storeItem={pkg} key={idx} onPurchase={() => onStoreItemPurchase(storeItem)} onOpenInvoice={onOpenInvoiceLink} />;
  }, []);

  if (!purchasableItems) {
    return null;
  }

  return (
    <div className="pb-2">
      <div className="flex justify-around bg-navy-300 rounded-8 text-center">
        <div
          className={`btn ${activeTab === 'all' ? 'neutral' : 'purple'} rounded-8`}
          onClick={() => setActiveTab('all')}
        >
          All
        </div>
        <div
          className={`btn ${activeTab === 'packages' ? 'neutral' : 'purple'} rounded-8`}
          onClick={() => setActiveTab('packages')}
        >
          Packages
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-2 max-h-[400px] overflow-y-scroll pb-8 scrollbar-hidden" ref={containerRef}>
        {purchasableItems[activeTab].map((pkg, idx) => (
          storeItemRenderer(pkg, idx)
        ))}
      </div>
    </div>
  );
}

export default StoreDrawerContent;
